import { createSlice } from "@reduxjs/toolkit";
import { sessionSlice } from "./sessionSlice";

const initialState = {
  spawnPoint: {
    x: 0,
    y: 0,
    z: 0,
  },
  scaling: {
    active: false,
    objects: [],
  },
  spawnRotation: 0,
  color: "",
  myIndex: null,
  canIGrab: false,
  isInVR: false,
  canGrabMultipleObjs: false,
  isOrbitControls: false,
  users: [],
  elements: [],
  pdb: null,
  atomRadiusScale: 0,
  renderScale: 0,
  stickRadiusScale: 0,
  rubbers: null,
  temperature: 0,
  maxTemperature: 0,
  updateRate: 0,
  gimbalOwner: null,
  isGimbalGrabbed: false,
};

export const sceneSlice = createSlice({
  name: "scene",
  initialState,
  reducers: {
    receivedSpawnPoint: (state, action) => {
      const data = action.payload;
      state.spawnPoint.x = data.x;
      state.spawnPoint.y = data.y;
      state.spawnPoint.z = data.z;
      state.spawnRotation = data.angle;
      state.color = data.color;
      state.myIndex = data.userIndex;
      state.canIGrab = data.canGrab;
    },
    receivedStartingPackage: (state, action) => {
      state.users = action.payload.users;
      state.elements = action.payload.elements;
      state.pdb = action.payload.pdb;
      state.canGrabMultipleObjs = action.payload.canGrabMultipleObjs;
      state.atomRadiusScale = action.payload.atomRadiusScale;
      state.stickRadiusScale = action.payload.stickRadiusScale;
      state.renderScale = action.payload.renderScale;
      state.rubbers = action.payload.rubbers;
      state.temperature = action.payload.temperature;
      state.maxTemperature = action.payload.maxTemperature;
      state.updateRate = action.payload.updateRate;
    },
    userAdded: (state, action) => {
      state.users.push({ ...action.payload, visible: true });
    },
    userUpdatedVisibility: (state, action) => {
      const user = state.users.find((user) => user.id === action.payload.id);
      if (user) {
        user.visible = action.payload.visibility;
      }
    },
    userRemoved: (state, action) => {
      const userIndex = state.users.findIndex((user) => user.id === action.payload.id);
      state.users.splice(userIndex, 1);

      state.elements.forEach((element) => {
        if (element.owner === action.payload.id) {
          element.owner = null;
          element.isGrabbed = false;
        }
      });
    },

    addedRubber: (state, action) => {
      state.rubbers.push(action.payload.rubber);
    },
    removedRubber: (state, action) => {
      const index = state.rubbers.findIndex((rubber) => rubber.id === action.payload.id);
      if (index >= 0) {
        state.rubbers.splice(index, 1);
      }
    },
    removeAllRubbers: (state, action) => {
      state.rubbers = [];
    },

    permissionUpdated: (state, action) => {
      state.canIGrab = action.payload.canGrab;
    },
    canGrabMultipleObjsUpdated: (state, action) => {
      state.canGrabMultipleObjs = action.payload.canGrabMultipleObjs;
    },
    changedCameraControls: (state, action) => {
      state.isOrbitControls = action.payload.isOrbitControls;
    },
    updatedTemp: (state, action) => {
      state.temperature = action.payload.temperature;
    },
    updatedRates: (state, action) => {
      state.updateRate = action.payload.rate;
    },
    emitUserPermissionUpdate: (state, action) => {
      const user = state.users.find((user) => user.id === action.payload.id);
      if (user) {
        user.canGrab = action.payload.canGrab;
      }
    },
    emitControllerConnect: (state, action) => {
      state.isInVR = true;
    },
    emitGrabbingMultipleObjs: (state, action) => {
      return;
    },
    emitControllerDisconnect: (state, action) => {
      state.isInVR = false;
    },
    emitControllerUpdate: (state, action) => {
      return;
    },
    emitElementSubmit: (state, action) => {
      return;
    },
    elementAdded: (state, action) => {
      state.elements.push(action.payload.element);
    },
    emitScaleUpdate: (state, action) => {
      return;
    },
    emitMoleculeScaleUpdate: (state, action) => {
      return;
    },
    emitMoleculeScaleSet: (state, action) => {
      return;
    },
    emitUpdateRatesChange: (state, action) => {
      return;
    },
    emitTemperatureChange: (state, action) => {
      return;
    },
    emitTemperatureSet: (state, action) => {
      return;
    },
    emitSetAtomRadiusFactors: (state, action) => {
      return;
    },
    emitSetAtomColors: (state, action) => {
      return;
    },
    emitSetStickRadius: (state, action) => {
      return;
    },
    emitSetSelection: (state, action) => {
      return;
    },
    elementScaleUpdated: (state, action) => {
      const element = state.elements.find((element) => element.id === action.payload.element.id);
      if (element) {
        element.scale = action.payload.element.scale;
      }
    },
    atomsScaleUpdated: (state, action) => {
      state.renderScale = action.payload.renderScale;
    },
    elementEnableUpdated: (state, action) => {
      const element = state.elements.find((element) => element.id === action.payload.element.id);
      if (element) {
        element.isEnabled = action.payload.element.isEnabled;
      }
    },
    elementClearGrabbing: (state, action) => {
      const element = state.elements.find((element) => element.id === action.payload.id);
      if (element) {
        element.owner = null;
        element.isGrabbed = false;
      }
    },
    updateElementList: (state, action) => {
      state.elements = action.payload.elements;
    },
    emitElementDelete: (state, action) => {
      return;
    },
    elementDeleted: (state, action) => {
      const elementIndex = state.elements.findIndex((element) => element.id === action.payload.id);
      state.elements.splice(elementIndex, 1);
    },
    emitElementRecenter: (state, action) => {
      return;
    },
    emitElementEnable: (state, action) => {
      return;
    },
    emitElementGrabbed: (state, action) => {
      return;
    },
    emitElementUngrabbed: (state, action) => {
      return;
    },
    elementGrabbingUpdated: (state, action) => {
      const element = state.elements.find((element) => element.id === action.payload.element.id);
      if (element) {
        element.isGrabbed = action.payload.element.isGrabbed;
        element.owner = action.payload.element.owner;
      }
    },
    emitElementUpdate: (state, action) => {
      return;
    },
    startScaling: (state, action) => {
      state.scaling.active = true;
      state.scaling.objects = action.payload;
    },
    stopScaling: (state, action) => {
      state.scaling.active = false;
      state.scaling.objects = [];
    },
    joystickUpdate: (state, action) => {
      return;
    },
    joystickEnd: (state, action) => {
      return;
    },
    emitSlideUpdate: (state, action) => {
      return;
    },
    enterVR: (state, action) => {
      return;
    },
    enterAR: (state, action) => {
      return;
    },
    setupScene: (state, action) => {
      return;
    },
    emitRemotePinchStart: (state, action) => {
      return;
    },
    emitRemotePinchEnd: (state, action) => {
      return;
    },
    // Gimbal actions
    emitGimbalGrabbed: (state, action) => {
      return;
    },
    emitGimbalUngrabbed: (state, action) => {
      return;
    },
    emitGimbalUpdate: (state, action) => {
      return;
    },
    gimbalGrabbingUpdated: (state, action) => {
      state.gimbalOwner = action.payload.owner;
      state.isGimbalGrabbed = action.payload.isGrabbed;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sessionSlice.actions.connectionLost, (state, action) => {
      state.spawnPoint = {
        x: 0,
        y: 0,
        z: 0,
      };
      state.spawnRotation = 0;
      state.color = "";
      state.myIndex = null;
      state.canIGrab = false;
      state.users = [];
      state.elements = [];
      state.gimbalOwner = null;
      state.isGimbalGrabbed = false;
    });

    builder.addCase(sessionSlice.actions.sessionClosed, (state, action) => {
      state.spawnPoint = {
        x: 0,
        y: 0,
        z: 0,
      };
      state.spawnRotation = 0;
      state.color = "";
      state.myIndex = null;
      state.canIGrab = false;
      state.users = [];
      state.elements = [];
      state.gimbalOwner = null;
      state.isGimbalGrabbed = false;
    });

    builder.addCase(sessionSlice.actions.requestTrial, (state, action) => {
      state.spawnRotation = Math.PI;
      state.myIndex = 0;
      state.canIGrab = true;
    });

    builder.addCase(sessionSlice.actions.leaveTrial, (state, action) => {
      state.spawnRotation = Math.PI;
      state.myIndex = null;
      state.canIGrab = false;
    });
  },
});
